import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import Cookies from 'js-cookie';
import { refreshToken } from '../../../../Redux/Auth/authActions';
import { mapSubmitProjectPayload, mapUpdateProjectPayload } from '../Storage/partnerMappers';
import { omitEmptyProjectUpdateFields } from '../Utils/partnerValidation';
import { partnerPortalApi } from '../Services/partnerPortalApi';
import { resolveAccessToken, usePartnerAccessToken } from './usePartnerAccessToken';
import {
  PARTNER_MY_PROJECTS_CACHE_KEY,
  readPartnerProjectListCache,
  writePartnerProjectListCache,
} from '../Utils/partnerProjectListCache';

const EMPTY_SUMMARY = {
  total: 0,
  active: 0,
  salesCycle: 0,
  completed: 0,
};

const isUnauthorizedError = (error) => {
  const message = (error?.message || '').toLowerCase();
  return message.includes('unauthorized') || message.includes('authentication');
};

export const usePartnerProjects = () => {
  const dispatch = useDispatch();
  const accessToken = usePartnerAccessToken();
  const [projects, setProjects] = useState([]);
  const [summary, setSummary] = useState(EMPTY_SUMMARY);
  const [partnerAdoptionEngineers, setPartnerAdoptionEngineers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const withAuthRetry = useCallback(async (apiCall) => {
    const token = resolveAccessToken(accessToken);
    try {
      return await apiCall(token);
    } catch (err) {
      if (!isUnauthorizedError(err) || !Cookies.get('refreshToken')) {
        throw err;
      }

      await dispatch(refreshToken());
      const refreshedToken = Cookies.get('accessToken');
      if (!refreshedToken) {
        throw new Error('Your session has expired. Please sign in again.');
      }

      return apiCall(refreshedToken);
    }
  }, [accessToken, dispatch]);

  const refreshProjects = useCallback(async () => {
    const token = resolveAccessToken(accessToken);
    if (!token) {
      setProjects([]);
      setSummary(EMPTY_SUMMARY);
      setPartnerAdoptionEngineers([]);
      setLoading(false);
      return;
    }

    const cached = readPartnerProjectListCache(PARTNER_MY_PROJECTS_CACHE_KEY);
    if (cached) {
      setProjects(cached.projects || []);
      setPartnerAdoptionEngineers(cached.partnerAdoptionEngineers || []);
      setSummary({
        total: cached.summary?.total ?? 0,
        active: cached.summary?.active ?? 0,
        salesCycle: cached.summary?.salesCycle ?? 0,
        completed: cached.summary?.completed ?? 0,
      });
      setLoading(false);
    } else {
      setLoading(true);
    }
    setError(null);

    try {
      const data = await withAuthRetry((activeToken) => (
        partnerPortalApi.fetchMyProjects(activeToken)
      ));
      writePartnerProjectListCache(PARTNER_MY_PROJECTS_CACHE_KEY, data);
      setProjects(data.projects || []);
      setPartnerAdoptionEngineers(data.partnerAdoptionEngineers || []);
      setSummary({
        total: data.summary?.total ?? 0,
        active: data.summary?.active ?? 0,
        salesCycle: data.summary?.salesCycle ?? 0,
        completed: data.summary?.completed ?? 0,
      });
    } catch (err) {
      setError(err.message);
      setProjects([]);
      setSummary(EMPTY_SUMMARY);
      setPartnerAdoptionEngineers([]);
    } finally {
      setLoading(false);
    }
  }, [accessToken, withAuthRetry]);

  useEffect(() => {
    refreshProjects();
  }, [refreshProjects]);

  const createProject = useCallback(async (payload) => {
    return withAuthRetry((activeToken) => (
      partnerPortalApi.submitProject(activeToken, mapSubmitProjectPayload(payload))
    )).then(async (result) => {
      await refreshProjects();
      return result;
    });
  }, [refreshProjects, withAuthRetry]);

  const updateProject = useCallback(async (projectId, payload) => {
    const updatePayload = omitEmptyProjectUpdateFields(mapUpdateProjectPayload(payload));
    const updatedProject = await withAuthRetry((activeToken) => (
      partnerPortalApi.patchProject(activeToken, projectId, updatePayload)
    ));
    await refreshProjects();
    return updatedProject;
  }, [refreshProjects, withAuthRetry]);

  const deleteProject = useCallback(async (projectId) => {
    await withAuthRetry((activeToken) => (
      partnerPortalApi.deleteProject(activeToken, projectId)
    ));
    await refreshProjects();
  }, [refreshProjects, withAuthRetry]);

  return {
    projects,
    summary,
    partnerAdoptionEngineers,
    loading,
    error,
    refreshProjects,
    createProject,
    updateProject,
    deleteProject,
  };
};
